import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, Contact } from "lucide-react";
import { REFPROV } from "@/constants/testIds";
import {
  DEMO_NPI_SYSTEM,
  formatCityStateZip,
  listReferringProviders,
} from "@/lib/referringProviderApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load referring provider master list.";
}

function formatAddress(address) {
  if (!address) return "—";
  const street = (address.line || []).filter(Boolean).join(", ");
  const cityLine = formatCityStateZip(address);
  return [street, cityLine, address.country].filter(Boolean).join(", ") || "—";
}

export default function ReferringProvidersPage() {
  const [items, setItems] = useState([]);
  const [npiSystem, setNpiSystem] = useState(DEMO_NPI_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listReferringProviders({ active: true });
        if (cancelled) return;
        setItems(data.items || []);
        setNpiSystem(data.npiSystem || DEMO_NPI_SYSTEM);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emr-catalog emr-catalog--full-table" data-testid={REFPROV.page}>
      <div className="emr-catalog__pagehead">
        <h1 className="emr-catalog__title" data-testid={REFPROV.title}>
          Referring Providers
        </h1>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section emr-catalog__purpose">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Demo master list of external referring physicians. Identity is stored as
              Practitioner; organization, mailing address, phone, and fax live on
              PractitionerRole. Not connected to Encounter or Calendar yet, and not
              used for billing.
            </p>
          </section>

          <section
            data-testid={REFPROV.banner}
            className="card emr-catalog__section emr-catalog__notice emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Fictional demo records only. NPI is the non-billable token DEMO ({npiSystem}),
              not a real National Provider Identifier. Do not use these rows for claims.
            </p>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Referring physicians</h2>
          {error ? (
            <div data-testid={REFPROV.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button
                variant="outline"
                className="btn emr-catalog__retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={REFPROV.loading} className="emr-catalog__status-msg">
              Loading referring providers…
            </div>
          ) : !items.length ? (
            <div data-testid={REFPROV.emptyState} className="emr-catalog__empty">
              <Contact className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No demo referring providers are seeded yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table
                data-testid={REFPROV.table}
                className="striped highlight emr-catalog__table emr-catalog__table--wide"
              >
                <thead>
                  <tr>
                    <th>Provider ID</th>
                    <th>Name</th>
                    <th>Credentials</th>
                    <th>Specialty</th>
                    <th>Organization</th>
                    <th>NPI</th>
                    <th>Mailing address</th>
                    <th>Phone</th>
                    <th>Fax</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id || row.providerId}
                      data-testid={`${REFPROV.row}-${row.providerId}`}
                    >
                      <td>{row.providerId}</td>
                      <td className="emr-catalog__name">
                        {row.firstName} {row.lastName}
                      </td>
                      <td>{row.credentials}</td>
                      <td>{row.specialty}</td>
                      <td className="emr-catalog__muted">{row.organization || "—"}</td>
                      <td>
                        <span className="emr-catalog__token">{row.npi || "DEMO"}</span>
                      </td>
                      <td className="emr-catalog__address emr-catalog__muted">
                        {formatAddress(row.address)}
                      </td>
                      <td>{row.phone || "—"}</td>
                      <td>{row.fax || "—"}</td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
